import React from "react";
import { motion } from "framer-motion";

type ButtonVariant = "primary" | "secondary" | "ghost";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  full?: boolean;
}

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-sage text-white shadow-[0_6px_20px_rgba(138,154,134,0.35)] hover:bg-sage-dark",
  secondary:
    "bg-white text-forest-900 border border-forest-900/10 hover:bg-cream-surface",
  ghost: "bg-transparent text-forest-700 hover:bg-forest-900/5",
};

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  full,
  className = "",
  children,
  ...props
}) => (
  <motion.button
    whileTap={{ scale: 0.97 }}
    {...(props as any)}
    className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-medium transition-colors duration-200 disabled:opacity-40 disabled:pointer-events-none ${
      variants[variant]
    } ${full ? "w-full" : ""} ${className}`}
  >
    {children}
  </motion.button>
);

export const Card: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className = "",
  children,
  ...props
}) => (
  <div
    {...props}
    className={`bg-white rounded-3xl border border-forest-900/5 shadow-[0_8px_32px_rgba(33,38,33,0.04)] ${className}`}
  >
    {children}
  </div>
);

export const Overline: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <p className={`text-xs uppercase tracking-[0.2em] font-medium text-forest-500 ${className}`}>
    {children}
  </p>
);

interface PageHeaderProps {
  overline?: string;
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ overline, title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-4 mb-8">
    <div className="min-w-0">
      {overline && <Overline className="mb-2">{overline}</Overline>}
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-forest-900 leading-tight">
        {title}
      </h1>
      {subtitle && <p className="mt-2 text-forest-500 leading-relaxed">{subtitle}</p>}
    </div>
    {right && <div className="shrink-0 pt-1">{right}</div>}
  </div>
);

export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  hint?: string;
}> = ({ icon, title, hint }) => (
  <div className="flex flex-col items-center text-center py-14 px-6">
    {icon && (
      <div className="mb-5 h-16 w-16 rounded-full bg-cream-surface flex items-center justify-center text-sage">
        {icon}
      </div>
    )}
    <p className="font-heading text-xl text-forest-800 font-light">{title}</p>
    {hint && <p className="mt-2 text-sm text-forest-500 max-w-[15rem] leading-relaxed">{hint}</p>}
  </div>
);
